<template>
  <a-modal
    v-model:visible="visible"
    title="添加关联客户"
    width="1000px"
    @ok="handleOk"
    @cancel="handleCancel"
    :confirm-loading="loading"
    ok-text="确定添加"
    cancel-text="取消"
  >
    <!-- 筛选条件 -->
    <div class="filter-section">
      <a-form :model="queryForm" layout="inline">
        <a-form-item label="客户编码">
          <a-input v-model="queryForm.customerCode" placeholder="请输入客户编码" allow-clear />
        </a-form-item>
        <a-form-item label="客户名称">
          <a-input v-model="queryForm.customerName" placeholder="请输入客户名称" allow-clear />
        </a-form-item>
        <a-form-item label="客户状态">
          <a-select v-model="queryForm.status" placeholder="请选择状态" allow-clear>
            <a-option value="">全部</a-option>
            <a-option value="0">启用</a-option>
            <a-option value="1">禁用</a-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch" :loading="tableLoading">
              <template #icon>
                <icon-search  style="stroke: #fff"/>
              </template>
              搜索
            </a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </div>

    <a-divider />

    <!-- 客户列表表格 -->
    <a-table
      row-key="id"
      :columns="columns"
      :data="pagination.records"
      :pagination="false"
      :loading="tableLoading"
      column-resizable
      :row-selection="{
        type: 'checkbox',
        showCheckedAll: true,
      }"
      v-model:selectedKeys="selectedRowKeys"
      size="small"
      :bordered="{cell:true}"
      :scroll="{ x: 800, y: 400 }"
    >
      <template #createTime="{ record }">
        {{ formatDate(record.createTime) }}
      </template>
    </a-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <a-pagination
        v-model:current="pagination.current"
        v-model:page-size="pagination.size"
        :total="pagination.total"
        :page-size-options="[10, 20, 50]"
        show-total
        show-jumper
        show-page-size
        @change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
    </div>

    <template #footer>
      <div class="modal-footer">
        <span class="selected-info">已选择 {{ selectedRowKeys.length }} 个客户</span>
        <div>
          <a-space>
            <a-button @click="handleCancel">取消</a-button>
            <a-button type="primary" @click="handleOk" :loading="loading" :disabled="selectedRowKeys.length === 0">
              确定添加
            </a-button>
          </a-space>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Message } from "@arco-design/web-vue"
import { getCustomerPage, addAccountCustomerRelation } from '@/apis/customer-data'
import { IconSearch } from '@arco-design/web-vue/es/icon'
import {timestampNumber2dateString} from "@/utils/date-util";

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  accountId: {
    type: [String, Number],
    default: null
  },
  customerIdList: {
    type: [Array],
    default: []
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const tableLoading = ref(false)
const selectedRowKeys = ref([])

// 查询表单
const queryForm = ref({
  customerCode: '',
  customerName: '',
  status: ''
})

// 分页数据
const pagination = ref({
  records: [],
  current: 1,
  size: 10,
  total: 0
})

// 表格列定义
const columns = ref([
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: '客户编码', dataIndex: 'customerCode', width: 120 },
  { title: '客户名称', dataIndex: 'customerName', width: 150, ellipsis: true, tooltip: true },
  { title: '联系人', dataIndex: 'contactPerson', width: 100 },
  { title: '联系电话', dataIndex: 'contactPhone', width: 130 },
  { title: '联系邮箱', dataIndex: 'contactEmail', width: 180, ellipsis: true, tooltip: true },
  // { title: '客户地址', dataIndex: 'address', width: 200, ellipsis: true, tooltip: true },
  // { title: '省份', dataIndex: 'province', width: 80 },
  // { title: '城市', dataIndex: 'city', width: 80 },
  // { title: '区县', dataIndex: 'district', width: 80 },
  // { title: '客户状态', dataIndex: 'status', width: 80 },
  // { title: '客户等级', dataIndex: 'level', width: 80 },
  // { title: '客户来源', dataIndex: 'source', width: 100 },
  { title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'createTime' }
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    resetData()
    fetchCustomerList()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 重置数据
const resetData = () => {
  selectedRowKeys.value = []
  queryForm.value = {
    customerCode: '',
    customerName: '',
    status: ''
  }
  pagination.value = {
    records: [],
    current: 1,
    size: 10,
    total: 0
  }
}

// 获取客户列表
const fetchCustomerList = async () => {
  tableLoading.value = true
  try {
    const response = await getCustomerPage({
      ...queryForm.value,
      current: pagination.value.current,
      size: pagination.value.size
    })
    if (response.code === 0) {
      pagination.value = response.data
      handleCheckboxStatus()
    } else {
      Message.error(response.message || '获取客户列表失败')
    }
  } catch (error) {
    Message.error('获取客户列表失败')
    console.error('获取客户列表失败:', error)
  } finally {
    tableLoading.value = false
  }
}

/**
 * 处理多选框禁用状态
 */
const handleCheckboxStatus = () => {
  pagination.value.records.forEach((item) => {
    if (props.customerIdList.includes(item.id)) {
      item.disabled = true
      console.log(item)
    }
  })
}

// 搜索
const handleSearch = () => {
  pagination.value.current = 1
  fetchCustomerList()
}

// 重置筛选条件
const handleReset = () => {
  queryForm.value = {
    customerCode: '',
    customerName: '',
    status: ''
  }
  handleSearch()
}

// 页码变化
const handlePageChange = () => {
  fetchCustomerList()
}

// 每页数量变化
const handlePageSizeChange = () => {
  pagination.value.current = 1
  fetchCustomerList()
}

// 确定添加
const handleOk = async () => {
  if (selectedRowKeys.value.length === 0) {
    Message.warning('请选择要添加的客户')
    return
  }

  loading.value = true
  try {
    const response = await addAccountCustomerRelation(props.accountId, selectedRowKeys.value)
    if (response.code === 0) {
      Message.success(`成功添加 ${selectedRowKeys.value.length} 个关联客户`)
      emit('success')
      handleCancel()
    } else {
      Message.error(response.message || '添加关联客户失败')
    }
  } catch (error) {
    Message.error('添加关联客户失败')
    console.error('添加关联客户失败:', error)
  } finally {
    loading.value = false
  }
}

// 取消
const handleCancel = () => {
  visible.value = false
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}
</script>

<style scoped>
.filter-section {
  margin-bottom: 16px;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.selected-info {
  color: #1890ff;
  font-size: 14px;
}
</style>